import { generateClient } from 'aws-amplify/api';
import { Expense } from '../../types/expense';

// Lazy initialize client to avoid "Amplify not configured" error
let client: ReturnType<typeof generateClient> | null = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// Flag to disable subscriptions (AppSync schema doesn't have subscriptions configured)
const SUBSCRIPTIONS_ENABLED = false;

// GraphQL subscription for expense status updates
export const EXPENSE_STATUS_UPDATED = `
  subscription ExpenseStatusUpdated($submitterId: ID) {
    expenseStatusUpdated(submitterId: $submitterId) {
      id
      title
      category
      amount
      date
      description
      paymentMethod
      status
      submitterId
      department
      createdAt
      updatedAt
      receipts {
        items {
          id
          fileName
          fileUrl
          fileType
          uploadedAt
        }
      }
      approvals {
        items {
          id
          status
          comments
          level
          timestamp
          approverId
        }
      }
    }
  }
`;

// GraphQL subscription for approval notifications
export const APPROVAL_REQUIRED = `
  subscription ApprovalRequired($approverId: ID) {
    approvalRequired(approverId: $approverId) {
      id
      title
      category
      amount
      date
      description
      paymentMethod
      status
      submitterId
      department
      createdAt
      updatedAt
      receipts {
        items {
          id
          fileName
          fileUrl
          fileType
          uploadedAt
        }
      }
      approvals {
        items {
          id
          status
          comments
          level
          timestamp
          approverId
        }
      }
    }
  }
`;

// Subscription service class for managing GraphQL subscriptions
export class SubscriptionService {
  private subscriptions: Map<string, any> = new Map();

  /**
   * Subscribe to expense status updates for a specific user
   * Requirements: 9.3 - Real-time expense status updates
   */
  subscribeToExpenseStatusUpdates(
    submitterId: string,
    onUpdate: (expense: Expense) => void,
    onError?: (error: any) => void
  ): () => void {
    // Subscriptions disabled - AppSync schema doesn't have subscriptions configured
    if (!SUBSCRIPTIONS_ENABLED) {
      console.log('[Subscriptions] Disabled - skipping expense status subscription');
      return () => {};
    }

    const subscriptionKey = `expense-status-${submitterId}`;
    
    try {
      // Amplify v6 subscription API returns an observable-like object
      const sub = getClient().graphql({
        query: EXPENSE_STATUS_UPDATED,
        variables: { submitterId }
      });

      // Check if it's a subscription (has subscribe method)
      if ('subscribe' in sub && typeof (sub as any).subscribe === 'function') {
        const subscription = (sub as any).subscribe({
          next: (result: { data?: { expenseStatusUpdated: Expense } }) => {
            if (result.data?.expenseStatusUpdated) {
              onUpdate(result.data.expenseStatusUpdated);
            }
          },
          error: (error: any) => {
            console.error('Expense status subscription error:', error);
            if (onError) {
              onError(error);
            }
          }
        });

        this.subscriptions.set(subscriptionKey, subscription);

        // Return unsubscribe function
        return () => {
          const storedSub = this.subscriptions.get(subscriptionKey);
          if (storedSub) {
            storedSub.unsubscribe();
            this.subscriptions.delete(subscriptionKey);
          }
        };
      }

      // Fallback: if not a subscription, return empty unsubscribe
      console.warn('GraphQL operation is not a subscription');
      return () => {};
    } catch (error) {
      console.error('Failed to create expense status subscription:', error);
      if (onError) {
        onError(error);
      }
      return () => {}; // Return empty unsubscribe function
    }
  }

  /**
   * Subscribe to approval notifications for a specific approver
   * Requirements: 9.3 - Real-time approval notifications
   */
  subscribeToApprovalNotifications(
    approverId: string,
    onApprovalRequired: (expense: Expense) => void,
    onError?: (error: any) => void
  ): () => void {
    // Subscriptions disabled - AppSync schema doesn't have subscriptions configured
    if (!SUBSCRIPTIONS_ENABLED) {
      console.log('[Subscriptions] Disabled - skipping approval notification subscription');
      return () => {};
    }

    const subscriptionKey = `approval-required-${approverId}`;
    
    try {
      const sub = getClient().graphql({
        query: APPROVAL_REQUIRED,
        variables: { approverId }
      });

      if ('subscribe' in sub && typeof (sub as any).subscribe === 'function') {
        const subscription = (sub as any).subscribe({
          next: (result: { data?: { approvalRequired: Expense } }) => {
            if (result.data?.approvalRequired) {
              onApprovalRequired(result.data.approvalRequired);
            }
          },
          error: (error: any) => {
            console.error('Approval notification subscription error:', error);
            if (onError) {
              onError(error);
            }
          }
        });

        this.subscriptions.set(subscriptionKey, subscription);

        return () => {
          const storedSub = this.subscriptions.get(subscriptionKey);
          if (storedSub) {
            storedSub.unsubscribe();
            this.subscriptions.delete(subscriptionKey);
          }
        };
      }

      console.warn('GraphQL operation is not a subscription');
      return () => {};
    } catch (error) {
      console.error('Failed to create approval notification subscription:', error);
      if (onError) {
        onError(error);
      }
      return () => {}; // Return empty unsubscribe function
    }
  }

  /**
   * Subscribe to all expense updates (for admin users)
   * Requirements: 9.3 - Real-time updates for admin dashboard
   */
  subscribeToAllExpenseUpdates(
    onUpdate: (expense: Expense) => void,
    onError?: (error: any) => void
  ): () => void {
    // Subscriptions disabled - AppSync schema doesn't have subscriptions configured
    if (!SUBSCRIPTIONS_ENABLED) {
      console.log('[Subscriptions] Disabled - skipping all expense updates subscription');
      return () => {};
    }

    const subscriptionKey = 'all-expense-updates';
    
    try {
      const sub = getClient().graphql({
        query: EXPENSE_STATUS_UPDATED
        // No variables means subscribe to all updates
      });

      if ('subscribe' in sub && typeof (sub as any).subscribe === 'function') {
        const subscription = (sub as any).subscribe({
          next: (result: { data?: { expenseStatusUpdated: Expense } }) => {
            if (result.data?.expenseStatusUpdated) {
              onUpdate(result.data.expenseStatusUpdated);
            }
          },
          error: (error: any) => {
            console.error('All expense updates subscription error:', error);
            if (onError) {
              onError(error);
            }
          }
        });

        this.subscriptions.set(subscriptionKey, subscription);

        return () => {
          const storedSub = this.subscriptions.get(subscriptionKey);
          if (storedSub) {
            storedSub.unsubscribe();
            this.subscriptions.delete(subscriptionKey);
          }
        };
      }

      console.warn('GraphQL operation is not a subscription');
      return () => {};
    } catch (error) {
      console.error('Failed to create all expense updates subscription:', error);
      if (onError) {
        onError(error);
      }
      return () => {}; // Return empty unsubscribe function
    }
  }

  /**
   * Unsubscribe from all active subscriptions
   * Requirements: 9.3 - Connection management
   */
  unsubscribeAll(): void {
    this.subscriptions.forEach((subscription, key) => {
      try {
        subscription.unsubscribe();
      } catch (error) {
        console.error(`Failed to unsubscribe from ${key}:`, error);
      }
    });
    this.subscriptions.clear();
  }

  /**
   * Get count of active subscriptions
   */
  getActiveSubscriptionCount(): number {
    return this.subscriptions.size;
  }

  /**
   * Check if a specific subscription is active
   */
  isSubscriptionActive(key: string): boolean {
    return this.subscriptions.has(key);
  }
}

// Export singleton instance
export const subscriptionService = new SubscriptionService();